import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Save, Loader2, Globe, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { AssignUsersSection } from '../components/workout-editor/AssignUsersSection';

const toList = (v) => (Array.isArray(v) ? v : v ? [v] : []);

import { 
  WorkoutBasicInfo, 
  VideoUploadSection, 
  ImageUploadSection,
  ExerciseEditor, 
  WorkoutPreviewSidebar 
} from '../components/workout-editor';

const LOGO_URL = '/logo.webp';

export const AdminWorkoutEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workout, setWorkout] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [savedAssignees, setSavedAssignees] = useState([]);

  const assignableUsers = useMemo(
    () => allUsers.filter(u => !u.is_admin),
    [allUsers],
  );
  const assignees = toList(workout?.assigned_to);
  const assignmentDirty = !isNew && [...assignees].sort().join() !== [...savedAssignees].sort().join();

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    
    if (id === 'new') {
      setIsNew(true);
      setWorkout({
        id: `workout-${Date.now()}`,
        title: 'New Workout',
        category: 'strength',
        difficulty: 'intermediate',
        duration_minutes: 30,
        calories_burn: 200,
        description: '',
        video_url: '',
        exercises: [],
        assigned_to: []
      });
      setLoading(false);
    } else {
      fetchWorkout();
    }
    fetchUsers();
  }, [id, user, navigate]);

  const fetchWorkout = async () => {
    try {
      const response = await api().get(`/admin/workouts/${id}`);
      const assigned = toList(response.data.assigned_to);
      setWorkout({ ...response.data, assigned_to: assigned });
      setSavedAssignees(assigned);
    } catch (e) {
      toast.error('Failed to load workout');
      navigate('/admin');
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await api().get('/admin/users');
      setAllUsers(res.data.users || res.data || []);
    } catch (e) {
      console.debug('Error:', e?.message);
    }
  };

  const handleSave = async () => {
    if (!workout.title.trim()) {
      toast.error('Please enter a workout title');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await api().post('/admin/workouts', workout);
        toast.success('Workout created successfully!');
        navigate('/admin');
      } else {
        const res = await api().put(`/admin/workouts/${id}`, workout);
        setSavedAssignees(toList(res.data?.workout?.assigned_to));
        toast.success('Workout saved successfully!');
      }
    } catch (err) {
      toast.error('Failed to save workout');
    }
    setSaving(false);
  };

  const updateField = (field, value) => {
    setWorkout(prev => ({ ...prev, [field]: value }));
  };

  const addExercise = () => {
    const newExercise = {
      name: 'New Exercise',
      sets: 3,
      reps: '10',
      duration_seconds: null,
      rest_seconds: 60
    };
    setWorkout(prev => ({
      ...prev,
      exercises: [...(prev.exercises || []), newExercise]
    }));
  };

  const updateExercise = (index, field, value) => {
    setWorkout(prev => {
      const newExercises = [...prev.exercises];
      newExercises[index] = { ...newExercises[index], [field]: value };
      return { ...prev, exercises: newExercises };
    });
  };

  const removeExercise = (index) => {
    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  if (loading || !workout) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="admin-workout-editor">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-primary transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span className="font-medium">Back to Admin</span>
            </Link>
          </div>
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          <Button 
            onClick={handleSave}
            className="bg-primary text-primary-foreground font-heading tracking-wider uppercase hover:bg-primary/90"
            disabled={saving}
            data-testid="save-workout-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isNew ? 'Create Workout' : 'Save Changes'}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <WorkoutBasicInfo 
              workout={workout} 
              updateField={updateField} 
              isNew={isNew} 
            />

            {/* Cover Photo */}
            <ImageUploadSection
              workout={workout}
              updateField={updateField}
              isNew={isNew}
            />
            
            {/* Video Section */}
            <div className="bg-card border border-border p-6">
              <VideoUploadSection 
                workout={workout}
                updateField={updateField}
                token={token}
                isNew={isNew}
              />
            </div>

            {/* Assign to User */}
            <AssignUsersSection
              users={assignableUsers}
              selected={assignees}
              onChange={(ids) => updateField('assigned_to', ids)}
              dirty={assignmentDirty}
              api={api}
            />

            <ExerciseEditor
              workout={workout}
              addExercise={addExercise}
              updateExercise={updateExercise}
              removeExercise={removeExercise}
            />

            {/* Premium gating */}
            <div className="bg-card border border-border p-6" data-testid="premium-gating-section">
              <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4 flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                Premium Access
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                When ON, only Premium subscribers can open this workout. Non-premium users still see it
                in the list with a lock badge — encouraging them to upgrade.
              </p>
              <button
                type="button"
                onClick={() => updateField('premium_only', !workout.premium_only)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md border transition-colors ${
                  workout.premium_only
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-black border-border text-white hover:border-white/30'
                }`}
                data-testid="premium-only-toggle"
              >
                <span className="flex items-center gap-2 font-heading uppercase tracking-wider text-sm">
                  {workout.premium_only ? <Lock className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                  {workout.premium_only ? 'Premium Members Only' : 'Available to Everyone'}
                </span>
                <span className={`h-6 w-11 rounded-full relative transition-colors ${workout.premium_only ? 'bg-primary' : 'bg-white/20'}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${workout.premium_only ? 'right-0.5' : 'left-0.5'}`} />
                </span>
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <WorkoutPreviewSidebar 
            workout={workout} 
            isNew={isNew} 
            id={id} 
          />
        </div>
      </main>
    </div>
  );
};
